<script>
import Layout from '@layouts/main'
import LayoutMemberPrepaid from '@layouts/LayoutMemberPrepaid'
import { merchantComputed } from '@state/helpers'
import formatDate, { dateDiffInMinutes } from '@utils/format-date'

export default {
  page() {
    return {
      title: this.$t('global.MEMBER_PREPAID'/* 預付方案 */),
    }
  },
  components: { Layout, LayoutMemberPrepaid },
  filters: {
    formatDate,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultImage: require('@assets/images/activity_default.png'),
    }
  },
  computed: {
    ...merchantComputed,
    orderTimeInterval() {
      return dateDiffInMinutes(this.order.startDate, this.order.endDate)
    },
    paramsSizeTotal() {
      let size = 0;

      this.order.params.forEach(param => {
        size += param.size * param.qty
      });

      return size
    },
    gender() {
      const gender = {
        male: this.$t('global.MALE' /* 先生 */),
        female: this.$t('global.FEMALE' /* 小姐 */),
      };
      return gender[this.order.gender]
    },
    paymentMethod() {
      const paymentMethod = {
        prepaid: this.$t('global.PAYMENT_METHOD_PREPAID' /* 預付抵用 */),
        cash: this.$t('global.PAYMENT_METHOD_CASH' /* 現金付款 */),
        creditCard: this.$t('global.PAYMENT_METHOD_CREDITCARD' /* 信用卡付款 */),
        free: this.$t('global.PAYMENT_METHOD_FREE' /* 免費 */),
      };
      return paymentMethod[this.order.paymentMethod]
    },
    isNullOrder() {
      return typeof this.order === 'object' && Object.keys(this.order).length === 0
    }
  },
  methods: {
    orderStatusText(orderStatus) {
      const status = [
        this.$t('global.CANCELED' /* 已取消 */),
        this.$t('global.PAID' /* 已付款 */),
        this.$t('global.UNPAID' /* 未付款 */),
      ];
      return status[orderStatus]
    },
    orderStatusClass(orderStatus) {
      const statusClassMap = {
        0: 'is-orange',
        1: 'is-primary',
        2: 'is-danger',
      };
      return statusClassMap[orderStatus]
    },
  },
}
</script>

<template>
  <Layout>
    <LayoutMemberPrepaid :order="order">
      <template v-if="!isNullOrder">
        <div :class="$style.wrapper">
          <div :class="$style.inner" class="is-mobile-column-reverse">
            <div class="columns is-multiline is-mobile is-mobile-column-reverse">
              <div class="column is-full-mobile is-half-tablet">
                <div class="media is-align-items-stretch">
                  <figure class="media-left">
                    <picture>
                      <BaseImage
                        :src="order.prepaid.introimage"
                        :error-src="defaultImage"
                        :class="$style.image"
                        class-name="is-cover"
                      />
                    </picture>
                  </figure>
                  <div class="media-content">
                    <div :class="$style.titleBox" class="level">
                      <div :class="$style.title" class="level-left">
                        <b-tag :type="orderStatusClass(order.status)" rounded style="margin-right: 0.5rem">
                          {{orderStatusText(order.status) }}
                        </b-tag> {{ order.prepaid.title }}
                      </div>
                    </div>
                    <div class="has-text-grey-light is-text-content">
                      {{ order.merchant.name }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="column is-full-mobile is-half-tablet">
                <div>
                  <div class="level" style="margin-bottom: 0">
                    <div class="level-left"></div>
                    <div class="level-right">
                      <div class="has-text-grey-light" style="margin-bottom: 1rem">{{ $t('global.ORDER_NO'/*訂單編號*/) }} #{{order.orderNo}}</div>
                    </div>
                  </div>
                  <div class="level">
                    <div class="level-left"></div>
                    <div class="level-right">
                      <div v-if="order.status !== 2"
                           :class="[$style.styleBlockItem, { [$style.isDisabled]: order.status === 0 }]">
                        <div class="level is-mobile">
                          <div class="level-left">
                            <span class="has-text-grey-light" style="margin-right: 0.5rem">{{ $t('global.REMAINDER_DEDUCT'/*剩餘抵用次數*/) }}</span>
                          </div>
                          <div class="level-right">
                          <span :class="order.status === 0 ? 'has-text-grey-light' : 'has-text-primary'"
                                class="is-size-4">
                            {{order.times - order.used}}/{{order.times}}
                          </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div :class="$style.inner">
            <div class="columns is-multiline is-mobile">
              <div class="column is-one-fifth-tablet is-full-mobile">
                {{ $t('global.ORDER_DETAIL'/*訂單明細*/) }}
              </div>
              <div class="column">
                <div class="level is-mobile" style="margin-bottom: 0.5rem;">
                  <div class="level-left">
                    {{order.prepaid.title}} x 1
                  </div>
                  <div class="level-right">
                <span class="has-text-right has-text-grey" style="min-width: 80px;">
                 ${{order.price}}
                </span>
                  </div>
                </div>
                <hr :class="$style.hr">
                <div class="level is-mobile" >
                  <div class="level-left">
                    {{ $t('global.TOTAL'/*總計*/) }}
                  </div>
                  <div class="level-right">
                <span class="has-text-right is-size-6" style="min-width: 80px;">
                  <span v-if="order.prepaid.basePrice > order.price" :class="$style['basePrice']" class="has-text-grey-light">原價 {{Math.floor(order.prepaid.basePrice)}}</span>
                  ${{order.price}}
                </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div :class="$style.inner">
            <div class="columns is-multiline is-mobile">
              <div class="column is-one-fifth-tablet is-full-mobile">
                {{ $t('global.ORDER_INFO'/*訂單資訊*/) }}
              </div>
              <div class="column is-text-content has-text-grey">
                <div v-if="order.created">
                  <span>{{ $t('global.ORDER_CREATED'/*訂單成立時間*/) }}：</span>
                  <span>{{order.created | formatDate('dateTimeWeekDay')}}</span>
                </div>
                <div v-if="order.paidDate">
                  <span>{{ $t('global.ORDER_PAID'/*訂單完成時間*/) }}：</span>
                  <span>{{order.paidDate | formatDate('dateTimeWeekDay')}}</span>
                </div>
                <div v-if="order.cancelDate">
                  <span>{{ $t('global.ORDER_CANCEL'/*訂單取消時間*/) }}：</span>
                  <span>{{order.cancelDate | formatDate('dateTimeWeekDay')}}</span>
                </div>
                <div v-if="order.paymentMethod">
                  <span>{{ $t('global.PAYMENT_METHOD'/*付款方式*/) }}：</span>
                  <span>{{paymentMethod}}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- <div :class="$style.inner">
            <div :class="$style.columnLeft">
              購買資訊
            </div>
            <div :class="$style.columnRight" class="is-text-content has-text-grey">
              <div>
                會員姓名：{{order.fullName}} {{gender}}
              </div>
              <div>
                會員電話：{{order.phoneCode}} {{order.phoneNumber}}
              </div>
              <div>
                會員Email：{{order.email}}
              </div>
            </div>
          </div> -->
        </div>
      </template>
    </LayoutMemberPrepaid>
  </Layout>
</template>
<style lang="scss" module>
  .wrapper {
    &:not(:last-child) {
      .inner {
        border-bottom: 1px solid $color-border-base;
      }
    }
    .inner:not(:last-child) {
      border-bottom: 1px solid $color-border-base;
    }

    .inner {
      padding: 1rem 0 0.9rem;
      margin: 0 1.5rem;
    }
  }

/*  .columnLeft {
    flex: 0 0 20.5%;
    max-width: 20.5%;
    -webkit-box-flex: 0;
    position: relative;
    width: 100%;
    @include mobile {
      flex: 0 0 100%;
      max-width: 100%;
      margin-bottom: 1rem;
    }
  }
  .columnRight {
    flex: 0 0 79.5%;
    max-width: 79.5%;
    -webkit-box-flex: 0;
    position: relative;
    width: 100%;
    @include mobile {
      flex: 0 0 100%;
      max-width: 100%;
      margin-bottom: 1rem;
    }
  }*/

  .titleBox {
    align-items: flex-start;
    margin-bottom: 1rem;
    .title {
      max-width: 388px;
      align-items: flex-start;
    }
  }

  .styleBlockItem {
    border: 1px solid $color-primary;
    border-radius: 5px;
    padding: 0.75rem 1rem;
    text-align: left;
    &.isDisabled {
      border: 1px solid #b5b5b5;
    }
  }

  .image {
    height: 100px;
    width: 150px;
    overflow: hidden;
    background: $grey-lighter no-repeat;
    border-radius: $size-image-border-radius;
    position: relative;
    background-size: cover;
    @include mobile {
      width: 100px;
      height: 70px;
    }
  }
  .basePrice {
    font-size: 13px;
    text-decoration: line-through;
    margin-right: 1rem;
  }
  .hr {
    border-bottom: 1px dashed $color-border-base;
    margin: 1rem 0;
    background: transparent;
  }
</style>
